import { useCallback, useMemo } from 'react';

import type { ReviewLanguageCode } from 'models/api';
import type { LanguageCode, MarketCode } from 'models/sitecore';

import { useLocale } from '../useSitecoreContext';

/**
 * Format a language or region code to its full name according to the current
 * locale.
 *
 * @example
 *
 * const formatLanguage = useLocaleFormatter('language');
 * formatLanguage('en')
 * // => 'Engelska'
 *
 * const formatRegion = useLocaleFormatter('region');
 * formatRegion('SE')
 * // => 'Sverige'
 */
export function useLocaleFormatter<T extends 'language' | 'region'>(type: T) {
	const { locale } = useLocale();

	const formatter = useMemo(
		() => new Intl.DisplayNames(locale, { type }),
		[locale, type],
	);

	return useCallback(
		(
			code: T extends 'language'
				? LanguageCode | ReviewLanguageCode
				: MarketCode,
		): string => formatter.of(code) ?? '',
		[formatter],
	);
}
